import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { AdminHeader, AdminTable } from './AdminShared';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ROLE_LABELS } from '@/lib/auth';
import { ADMIN } from '@/constants/testIds';

const EMPTY = {
  full_name: '', email: '', phone: '', role: 'audiologist',
  clinic_ids: [], home_clinic_id: '', skills: [], languages: [],
  shifts: [], color: '#0F766E', active: true,
};

export default function EmployeesTab() {
  const [rows, setRows] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    try {
      const [e, c] = await Promise.all([api.get('/employees'), api.get('/clinics')]);
      setRows(e.data); setClinics(c.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...EMPTY, ...r }); setOpen(true); };

  const save = async () => {
    try {
      const payload = {
        ...form,
        skills: typeof form.skills === 'string' ? form.skills.split(',').map(s => s.trim()).filter(Boolean) : form.skills,
        languages: typeof form.languages === 'string' ? form.languages.split(',').map(s => s.trim()).filter(Boolean) : form.languages,
      };
      if (editing) await api.patch(`/employees/${editing.id}`, payload);
      else await api.post('/employees', payload);
      toast.success('Saved');
      setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save'); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.full_name}?`)) return;
    await api.delete(`/employees/${r.id}`); load(); toast.success('Deleted');
  };
  const clinicNames = (ids) => (ids || []).map(id => clinics.find(c => c.id === id)?.name).filter(Boolean).join(', ');

  return (
    <div>
      <AdminHeader
        title="Staff / Employees"
        description="Audiologists, front desk, managers, and other clinic staff."
        onCreate={openCreate}
        createLabel="New Staff"
        createTestId={ADMIN.createButton('employee')}
      />
      <AdminTable
        loading={loading} rows={rows}
        columns={[
          { key: 'full_name', label: 'Name', render: (r) => (
            <div className="flex items-center gap-2">
              <span className="w-2 h-6 rounded-sm" style={{ backgroundColor: r.color || '#292524' }} />
              <span className="font-medium">{r.full_name}</span>
            </div>
          )},
          { key: 'role', label: 'Role', render: (r) => (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-stone-100 rounded font-semibold">
              {ROLE_LABELS[r.role] || r.role}
            </span>
          )},
          { key: 'email', label: 'Email' },
          { key: 'phone', label: 'Phone' },
          { key: 'clinics', label: 'Clinics', render: (r) => clinicNames(r.clinic_ids) || '—' },
        ]}
        rowTestId={ADMIN.row}
        onEdit={openEdit} onDelete={remove}
      />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Staff' : 'New Staff'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Full Name</Label>
              <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="h-9" data-testid="employee-field-name" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-9" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-9" />
              </div>
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Home Clinic</Label>
              <Select value={form.home_clinic_id || ''} onValueChange={(v) => setForm({ ...form, home_clinic_id: v, clinic_ids: [...new Set([...(form.clinic_ids||[]), v])] })}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Select clinic" /></SelectTrigger>
                <SelectContent>
                  {clinics.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Skills (comma-separated)</Label>
              <Input value={Array.isArray(form.skills) ? form.skills.join(', ') : form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} className="h-9" />
            </div>
            <div>
              <Label>Languages (comma-separated)</Label>
              <Input value={Array.isArray(form.languages) ? form.languages.join(', ') : form.languages} onChange={(e) => setForm({ ...form, languages: e.target.value })} className="h-9" />
            </div>
            <div>
              <Label>Colour tag</Label>
              <Input type="color" value={form.color || '#0F766E'} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-9 w-20 p-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid={ADMIN.saveButton}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
